<template>
  <div ref="calendarElement" />
</template>

<script setup lang="ts">
import { ref, Ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Calendar } from '@fullcalendar/core';

const props = defineProps({
  events: {
    type: Array<any>,
    default: () => [],
  },

  options: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['view-render', 'event-selected', 'date-clicked']);

const calendar: Ref<any> = ref(null);
const calendarElement: Ref<HTMLDivElement | null> = ref(null);

const initialize = () => {
  if (!calendarElement.value) {
    return;
  }

  const defaultConfig = {
    themeSystem: 'standard',
    datesSet: (info: any) => {
      viewRender(info.view);
    },
    eventClick: (eventClickInfo: any) => {
      eventSelected(eventClickInfo.event);
    },
    dateClick: (info: any) => {
      dateClicked(info.dateStr);
    },
  };

  const config = props.options
    ? {
        ...props.options,
        ...defaultConfig,
      }
    : defaultConfig;

  calendar.value = new Calendar(calendarElement.value, config);
  calendar.value.render();

  if (props.events) {
    calendar.value.removeAllEventSources();
    calendar.value.addEventSource(props.events);
  }
};

const viewRender = (view: any) => {
  emit('view-render', view);
};

const eventSelected = (event: any) => {
  emit('event-selected', event);
};

const dateClicked = (selectedDate: string) => {
  emit('date-clicked', selectedDate);
};

watch(
  () => props.events,
  (value) => {
    calendar.value.removeAllEventSources();
    calendar.value.addEventSource(value);
  },
  {
    deep: true,
  },
);

onMounted(() => {
  if (calendarElement.value?.offsetParent) {
    initialize();
  }
});

onBeforeUnmount(() => {
  if (calendar.value) {
    calendar.value.destroy();
    calendar.value = null;
  }
});
</script>
